import * as THREE from 'three';
import { GUARDIAN_POSITION } from './guardianScene';

// All effects follow the committed attack clock. Geometry is allocated once;
// the flying die, impact burst, and damage splat share the same hit instant.
export const createGuardianStrikeScene = (
  scene,
  {
    target = GUARDIAN_POSITION,
    scale = 1,
    name = 'guardian-player-strike',
  } = {}
) => {
  const effect = new THREE.Group();
  effect.name = name;
  effect.visible = false;
  scene.add(effect);
  const charge = new THREE.Group();
  const wake = new THREE.Group();
  const impact = new THREE.Group();
  effect.add(charge, wake, impact);
  const material = (color, opacity = 1) =>
    new THREE.MeshBasicMaterial({
      color,
      opacity,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
  const cyan = material('#67fff0');
  const magenta = material('#ff5be5');
  const white = material('#edffff');
  const ringGeometry = new THREE.RingGeometry(0.88, 1, 48);
  const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
  const chargeRings = [cyan, magenta, white].map((surface) => {
    const ring = new THREE.Mesh(ringGeometry, surface);
    charge.add(ring);
    return ring;
  });
  const shellMaterial = material('#b5fffa', 0.4);
  shellMaterial.wireframe = true;
  const shell = new THREE.Mesh(boxGeometry, shellMaterial);
  charge.add(shell);
  const ghostMaterials = [material('#67fff0', 0.28), material('#ff5be5', 0.24)];
  ghostMaterials.forEach((surface) => {
    surface.wireframe = true;
  });
  const ghosts = ghostMaterials.map((surface) => {
    const ghost = new THREE.Mesh(boxGeometry, surface);
    effect.add(ghost);
    return ghost;
  });
  const pixels = Array.from({ length: 28 }, (_, index) => {
    const pixel = new THREE.Mesh(boxGeometry, index % 2 ? magenta : cyan);
    wake.add(pixel);
    return pixel;
  });
  const trailMaterial = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const trailPositions = new THREE.BufferAttribute(new Float32Array(24 * 6), 3);
  const trailColors = new THREE.BufferAttribute(new Float32Array(24 * 6), 3);
  const trailGeometry = new THREE.BufferGeometry();
  trailGeometry.setAttribute('position', trailPositions);
  trailGeometry.setAttribute('color', trailColors);
  const trails = new THREE.LineSegments(trailGeometry, trailMaterial);
  trails.frustumCulled = false;
  effect.add(trails);
  const history = Array.from({ length: 25 }, () => new THREE.Vector3());
  const impactMaterials = ['#67fff0', '#ff5be5', '#edffff'].map((color) =>
    material(color)
  );
  const impactRings = impactMaterials.map((surface) => {
    const ring = new THREE.Mesh(ringGeometry, surface);
    impact.add(ring);
    return ring;
  });
  const fragments = Array.from({ length: 32 }, (_, index) => {
    const fragment = new THREE.Mesh(boxGeometry, impactMaterials[index % 3]);
    impact.add(fragment);
    return fragment;
  });
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const clamp = THREE.MathUtils.clamp;
  const color = new THREE.Color();
  let identity = null;

  return {
    update({ combat, elapsed, die, camera, reduceMotion }) {
      effect.visible = !!combat && elapsed < combat.hitAt + 850;
      if (!effect.visible) return;
      if (identity !== combat.combatId) {
        identity = combat.combatId;
        history.forEach((point) => point.copy(die.position));
      }
      const charging = elapsed < combat.launchAt;
      const flying = elapsed >= combat.launchAt && elapsed < combat.hitAt;
      const chargeProgress = clamp(
        elapsed / Math.max(1, combat.launchAt),
        0,
        1
      );
      const hit = clamp((elapsed - combat.hitAt) / 850, 0, 1);
      const power =
        (0.8 + clamp((combat.effectiveRoll || 0) / 180, 0, 1) * 0.55) * scale;
      const time = elapsed / 1000;
      charge.visible = charging && !reduceMotion;
      charge.position.copy(die.position);
      charge.quaternion.copy(camera.quaternion);
      chargeRings.forEach((ring, index) => {
        const radius = (2.4 - chargeProgress * 1.65 + index * 0.35) * power;
        ring.scale.set(radius, radius * (index === 1 ? 0.35 : 0.75), 1);
        ring.rotation.z = (index % 2 ? -1 : 1) * time * 2 + index;
        ring.position.z = 0.05 + index * 0.08;
      });
      shell.quaternion.copy(die.quaternion);
      shell.rotateY(time * 3);
      shell.scale.setScalar((2.5 - chargeProgress * 0.7) * power);
      cyan.opacity = 0.5 + chargeProgress * 0.4;
      magenta.opacity = 0.45 + chargeProgress * 0.35;
      white.opacity = 0.35 + chargeProgress * 0.5;
      ghosts.forEach((ghost, index) => {
        ghost.visible = !reduceMotion && (charging || flying);
        ghost.position.copy(die.position);
        ghost.position.x +=
          (index ? -1 : 1) * (0.12 + chargeProgress * 0.1) * scale;
        ghost.position.z += index * 0.24 * scale;
        ghost.quaternion.copy(die.quaternion);
        ghost.scale.set(1.9 + index * 0.15, 1.7, 1.9).multiplyScalar(scale);
      });
      wake.visible = !reduceMotion && (charging || flying);
      wake.position.copy(die.position);
      wake.quaternion.copy(camera.quaternion);
      pixels.forEach((pixel, index) => {
        const angle = index * 2.39996 + time * (flying ? 5 : 2);
        const radius =
          (charging ? 2.5 - chargeProgress * 1.5 : 0.6 + (index % 4) * 0.22) *
          power;
        pixel.position.set(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius,
          flying ? 0.6 + (index % 7) * 0.5 : (index % 3) * 0.12
        );
        pixel.rotation.z = index * 0.7;
        pixel.scale.set(
          flying ? 0.1 : 0.22 + (index % 3) * 0.12,
          0.04 + (index % 2) * 0.06,
          flying ? 0.6 + (index % 4) * 0.3 : 0.06
        );
      });
      trails.visible = flying && !reduceMotion;
      if (trails.visible) {
        for (let index = history.length - 1; index > 0; index -= 1)
          history[index].copy(history[index - 1]);
        history[0].copy(die.position);
        right.set(1, 0, 0).applyQuaternion(camera.quaternion);
        up.set(0, 1, 0).applyQuaternion(camera.quaternion);
        for (let index = 0; index < 24; index += 1) {
          const side = index % 2 ? -1 : 1;
          const a = history[index];
          const b = history[index + 1];
          const offset = 0.22 + index * 0.025;
          trailPositions.setXYZ(
            index * 2,
            a.x + right.x * offset * side,
            a.y + up.y * offset * side,
            a.z
          );
          trailPositions.setXYZ(
            index * 2 + 1,
            b.x + right.x * offset * side,
            b.y + up.y * offset * side,
            b.z
          );
          color
            .set(index % 2 ? '#ff5be5' : '#67fff0')
            .multiplyScalar(1 - index / 28);
          trailColors.setXYZ(index * 2, color.r, color.g, color.b);
          trailColors.setXYZ(index * 2 + 1, color.r, color.g, color.b);
        }
        trailPositions.needsUpdate = true;
        trailColors.needsUpdate = true;
      }
      impact.visible = elapsed >= combat.hitAt && hit < 1;
      impact.position.copy(target);
      impact.position.z += 0.8 * scale;
      impact.quaternion.copy(camera.quaternion);
      impactMaterials.forEach((surface, index) => {
        surface.opacity =
          (1 - hit) * (reduceMotion ? 0.35 : 0.85 - index * 0.12);
      });
      impactRings.forEach((ring, index) => {
        const spread = reduceMotion
          ? 1.5
          : (0.5 + hit * (6 + index * 1.8)) * power;
        ring.scale.set(spread, spread * (index === 1 ? 0.32 : 0.8), 1);
        ring.rotation.z = index * 0.8;
        ring.position.z = index * 0.12;
      });
      fragments.forEach((fragment, index) => {
        fragment.visible = !reduceMotion;
        const angle = index * 2.39996;
        const spread = (0.3 + hit * (3.2 + (index % 5))) * power;
        fragment.position.set(
          Math.cos(angle) * spread,
          Math.sin(angle) * spread * 0.7,
          (index % 4) * 0.12
        );
        fragment.rotation.set(hit * 5 + index, angle, angle + hit * 2);
        fragment.scale.set(
          (0.25 + (index % 4) * 0.18) * (1 - hit),
          0.05 + (1 - hit) * 0.1,
          0.06
        );
      });
    },
    dispose() {
      const geometries = new Set();
      const materials = new Set();
      effect.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) materials.add(object.material);
      });
      scene.remove(effect);
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((surface) => surface.dispose());
    },
  };
};
